import { isAxiosError } from "axios";

import { ClientReportingScopeError, ClientReportingScopeResponse, FetchClientReportingScopeAction } from "store/types";
import { ApiServiceError } from "store/services";

import { AsyncActions, AsyncActionsParams } from "../base";

export interface IClientReportingActions {
  fetchReportingScope: FetchClientReportingScopeAction;
}

export class ClientReportingActions
  extends AsyncActions<IClientReportingActions>
  implements IClientReportingActions
{
  public constructor(params: AsyncActionsParams) {
    super(params);
  }

  public fetchReportingScope: FetchClientReportingScopeAction = async (clientId) => {
    const normalizedClientId = Number(clientId);

    if (!Number.isFinite(normalizedClientId) || normalizedClientId <= 0) {
      throw {
        status: 400,
        message: "Client identifier is invalid."
      } as ClientReportingScopeError;
    }

    const controller = this.addProcess("fetchReportingScope");

    try {
      const response = await this.api.get<ClientReportingScopeResponse>({
        url: `/api/v0.0.3/clients/${normalizedClientId}/reporting-scope`,
        signal: controller.signal
      });
      return response.data;
    } catch (error) {
      throw this.normalizeError(error);
    } finally {
      this.removeProcess("fetchReportingScope");
    }
  };

  private normalizeError(error: unknown): ClientReportingScopeError {
    if (error instanceof ApiServiceError) {
      const message = error.message || "Unable to load reporting data. Please try again later.";
      const timeout = error.status === 408 || message.toLowerCase().includes("timeout");

      return {
        status: timeout ? 408 : error.status,
        message,
        timeout: timeout || false,
        aborted: error.aborted
      };
    }

    if (isAxiosError(error)) {
      const timeout = error.code === "ECONNABORTED";
      const status = timeout ? 408 : error.response?.status;

      return {
        status,
        message: error.message || "Unable to load reporting data. Please try again later.",
        timeout
      };
    }

    if (typeof error === "object" && error && "status" in error && "message" in error) {
      return error as ClientReportingScopeError;
    }

    return {
      message: error instanceof Error ? error.message : "Unable to load reporting data. Please try again later."
    };
  }
}
